import { flushSync } from 'react-dom';

interface ViewTransitionResult {
  finished: Promise<void>;
  ready?: Promise<void>;
  updateCallbackDone?: Promise<void>;
}

export const VIEW_TRANSITION_ACTIVE_ATTRIBUTE = 'data-view-transition-active';

/**
 * Starts a document view transition typed with `types`, so only elements whose
 * `view-transition-name` is gated on a matching `:active-view-transition-type`
 * rule participate.
 *
 * Never scope transitions to an element (`element.startViewTransition`):
 * scoped transitions abort with "Prepaint layout check failed" whenever the
 * update removes content. For a change confined to one list, pass the list as
 * `activeElement` instead: it carries `data-view-transition-active` for the
 * duration of the transition (including the old-state capture), letting CSS
 * name only that list's elements.
 */
export function startViewTransition(
  update: () => void,
  types: string[],
  activeElement?: Element | null,
): void {
  if (!('startViewTransition' in document) || typeof document.startViewTransition !== 'function') {
    update();
    return;
  }
  const runUpdate = () => {
    flushSync(() => update());
  };

  activeElement?.setAttribute(VIEW_TRANSITION_ACTIVE_ATTRIBUTE, '');
  const removeActiveAttribute = () => {
    activeElement?.removeAttribute(VIEW_TRANSITION_ACTIVE_ATTRIBUTE);
  };

  let transition: ViewTransitionResult;
  try {
    transition = document.startViewTransition({ update: runUpdate, types });
  } catch {
    // Engines that predate view-transition types only accept the bare
    // callback and throw synchronously on the options object form.
    try {
      transition = document.startViewTransition(runUpdate);
    } catch {
      removeActiveAttribute();
      update();
      return;
    }
  }

  // Transitions are aborted when superseded or when DOM state changes
  // mid-capture; an abort rejects all three transition promises.
  transition.finished.then(removeActiveAttribute, removeActiveAttribute);
  transition.ready?.catch(() => undefined);
  transition.updateCallbackDone?.catch(() => undefined);
}
